import { Alert, Button, Group, Stack, Stepper, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterConfigFields } from './AdapterConfigFields';
import { AdapterPickerGrid } from './AdapterPickerGrid';
import { getAdapterMeta, listAdapters } from './adapters/registry';
import { HmacSecretReveal } from './HmacSecretReveal';
import type { AdapterConfigField, AdapterKind, Integration } from './types';

export interface IntegrationFormSubmitPayload {
  title: string;
  kind: string;
  config: Record<string, unknown>;
  secrets?: Record<string, unknown>;
}

export type IntegrationFormMode = 'create' | 'edit';

export interface IntegrationFormProps {
  mode: IntegrationFormMode;
  /** Required for `edit` mode. */
  initialValues?: Integration;
  /**
   * Called with the validated payload. Parent should hit the API and resolve
   * with the created/updated Integration on success. Reject (or throw) on
   * server-side validation errors — the message is surfaced inline.
   */
  onSubmit: (payload: IntegrationFormSubmitPayload) => Promise<Integration>;
  onCancel: () => void;
}

interface FormShape {
  title: string;
  kind: string;
  config: Record<string, unknown>;
  secrets: Record<string, unknown>;
}

/**
 * Coerces a raw form value into the JSON shape expected by the backend.
 * - `json` field type → parsed object (or undefined when blank).
 * - `number` field type → number (or undefined).
 * - `array-string` / `email-list` → array (already from `TagsInput`).
 * - everything else → as-is.
 */
function coerceFieldValue(field: AdapterConfigField, raw: unknown): unknown {
  if (raw === undefined || raw === null) return undefined;
  if (field.type === 'json') {
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed.length === 0) return undefined;
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw; // backend will reject; client validator already surfaced the error
      }
    }
    return raw;
  }
  if (field.type === 'number') {
    if (raw === '') return undefined;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (field.type === 'checkbox') {
    return Boolean(raw);
  }
  if (field.type === 'email-list' || field.type === 'array-string') {
    return Array.isArray(raw) ? raw : [];
  }
  return raw;
}

/**
 * Shared create/edit form for integrations.
 *
 * Two-step `Stepper`:
 *   1. Adapter picker (skipped + locked in edit mode).
 *   2. Title + dynamic config + secrets fields.
 *
 * On successful create, if the response includes `hmac_secret_b64`, the form
 * stays mounted on a final "secret reveal" pane until the user dismisses it.
 */
export function IntegrationForm({ mode, initialValues, onSubmit, onCancel }: IntegrationFormProps) {
  const { t } = useTranslation();
  const adapters = useMemo(() => listAdapters(), []);

  const [step, setStep] = useState<number>(mode === 'edit' ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const form = useForm<FormShape>({
    mode: 'controlled',
    initialValues: {
      title: initialValues?.title ?? '',
      kind: initialValues?.kind ?? '',
      config: (initialValues?.config as Record<string, unknown>) ?? {},
      secrets: {},
    },
    validate: {
      title: (value) =>
        value && value.trim().length > 0 ? null : t('integrations.errors.titleRequired'),
    },
  });

  const selectedKind = form.values.kind;
  const selectedMeta = selectedKind ? getAdapterMeta(selectedKind) : undefined;

  const handlePickAdapter = (kind: string) => {
    form.setFieldValue('kind', kind as AdapterKind);
    // Reset config/secrets when the kind changes so leftover keys from a
    // previous selection don't leak into the payload.
    form.setFieldValue('config', {});
    form.setFieldValue('secrets', {});
    setStep(1);
  };

  const validateAdapterFields = (): boolean => {
    if (!selectedMeta) return false;
    const all: { field: AdapterConfigField; group: 'config' | 'secrets' }[] = [
      ...selectedMeta.configFields.map((field) => ({ field, group: 'config' as const })),
      ...selectedMeta.secretsFields.map((field) => ({ field, group: 'secrets' as const })),
    ];
    let ok = true;
    for (const { field, group } of all) {
      const value = (form.values[group] as Record<string, unknown>)[field.key];
      if (field.required && (value === undefined || value === null || value === '')) {
        form.setFieldError(`${group}.${field.key}`, t('integrations.errors.fieldRequired'));
        ok = false;
        continue;
      }
      if (field.validate) {
        const msg = field.validate(value);
        if (msg) {
          form.setFieldError(`${group}.${field.key}`, t(msg, msg));
          ok = false;
        }
      }
    }
    return ok;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!selectedMeta) {
      setError(t('integrations.errors.adapterRequired'));
      return;
    }
    const titleValidation = form.validate();
    if (titleValidation.hasErrors) return;
    if (!validateAdapterFields()) return;

    const config: Record<string, unknown> = {};
    for (const field of selectedMeta.configFields) {
      const v = coerceFieldValue(field, form.values.config[field.key]);
      if (v !== undefined) config[field.key] = v;
    }
    const secrets: Record<string, unknown> = {};
    for (const field of selectedMeta.secretsFields) {
      const v = coerceFieldValue(field, form.values.secrets[field.key]);
      if (v !== undefined) secrets[field.key] = v;
    }

    const payload: IntegrationFormSubmitPayload = {
      title: form.values.title.trim(),
      kind: selectedMeta.kind,
      config,
      secrets: Object.keys(secrets).length > 0 ? secrets : undefined,
    };

    try {
      setSubmitting(true);
      const result = await onSubmit(payload);
      if (mode === 'create' && result.hmac_secret_b64) {
        setRevealedSecret(result.hmac_secret_b64);
        setStep(2);
        return;
      }
      onCancel();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (revealedSecret) {
    return (
      <Stack gap="md">
        <Title order={3}>{t('integrations.form.secretRevealedTitle')}</Title>
        <HmacSecretReveal secret={revealedSecret} />
        <Group justify="flex-end">
          <Button onClick={onCancel}>{t('integrations.actions.done')}</Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Title order={3}>
        {mode === 'create' ? t('integrations.form.createTitle') : t('integrations.form.editTitle')}
      </Title>

      <Stepper
        active={step}
        onStepClick={mode === 'edit' ? undefined : setStep}
        allowNextStepsSelect={false}
      >
        <Stepper.Step
          label={t('integrations.form.stepKind')}
          description={t('integrations.form.stepKindDesc')}
          allowStepSelect={mode !== 'edit'}
        >
          <Stack mt="md">
            <Text size="sm" c="dimmed">
              {t('integrations.form.pickAdapterHint')}
            </Text>
            <AdapterPickerGrid
              adapters={adapters}
              selected={selectedKind}
              onSelect={handlePickAdapter}
            />
          </Stack>
        </Stepper.Step>

        <Stepper.Step
          label={t('integrations.form.stepConfigure')}
          description={t('integrations.form.stepConfigureDesc')}
          allowStepSelect={false}
        >
          <Stack mt="md" gap="md">
            {selectedMeta ? (
              <>
                <Text size="sm" c="dimmed">
                  {t(selectedMeta.description)}
                </Text>
                <TextInput
                  label={t('integrations.fields.integrationTitle')}
                  description={t('integrations.fields.integrationTitleHelp')}
                  required
                  disabled={submitting}
                  {...form.getInputProps('title')}
                />
                <AdapterConfigFields
                  fields={selectedMeta.configFields}
                  pathPrefix="config"
                  form={form}
                  disabled={submitting}
                />
                {selectedMeta.secretsFields.length > 0 && (
                  <Stack gap="xs">
                    <Text fw={500} size="sm">
                      {t('integrations.form.secretsHeader')}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t('integrations.form.secretsHelp')}
                    </Text>
                    <AdapterConfigFields
                      fields={selectedMeta.secretsFields}
                      pathPrefix="secrets"
                      form={form}
                      disabled={submitting}
                    />
                  </Stack>
                )}
              </>
            ) : (
              <Text c="dimmed">{t('integrations.form.pickAdapterFirst')}</Text>
            )}
          </Stack>
        </Stepper.Step>
      </Stepper>

      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title={t('integrations.errors.submitFailed')}
        >
          {error}
        </Alert>
      )}

      <Group justify="space-between">
        <Button variant="subtle" onClick={onCancel} disabled={submitting}>
          {t('integrations.actions.cancel')}
        </Button>
        <Group>
          {step === 1 && mode === 'create' && (
            <Button variant="default" onClick={() => setStep(0)} disabled={submitting}>
              {t('integrations.actions.back')}
            </Button>
          )}
          {step === 0 && mode === 'create' && (
            <Button onClick={() => selectedMeta && setStep(1)} disabled={!selectedMeta}>
              {t('integrations.actions.next')}
            </Button>
          )}
          {step === 1 && (
            <Button onClick={handleSubmit} loading={submitting}>
              {mode === 'create'
                ? t('integrations.actions.create')
                : t('integrations.actions.save')}
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
}
